import type { ClosedPosition } from '../../api';
import { LOCALE_BY_LANGUAGE } from '../../components/format-money';
import type { SupportedLanguage } from '../../i18n';
import { localDayKey as dayKey } from '../shared/format-time';

/** How a day group of E9 is titled: « Aujourd'hui » / "Today", « Hier » / "Yesterday", or a date. */
type DayKind = 'today' | 'yesterday' | 'date';

/** One day group of E9 (mockup Historique: "Today", "Yesterday", then a date). */
export interface HistoryDay {
  /** Local calendar day, `YYYY-MM-DD`: a stable React key. */
  readonly key: string;
  readonly kind: DayKind;
  /** The localized date ("mercredi 14 octobre"), shown for `date` and kept for the others. */
  readonly dateLabel: string;
  readonly positions: readonly ClosedPosition[];
}

/** Inputs of {@link groupByDay}: the clock and the time zone are passed in so tests fix them. */
interface DayOptions {
  /** "Now" in epoch milliseconds (`Date.now()` on screen). */
  readonly now: number;
  readonly language: SupportedLanguage;
  /** IANA time zone; `undefined` is the device's, as on screen (M6 R7). */
  readonly timeZone?: string | undefined;
}

const DAY_LABELS = new Map<string, Intl.DateTimeFormat>();

function dayLabel(instant: number, { language, timeZone }: DayOptions, withYear: boolean): string {
  const cacheKey = `${language}|${timeZone ?? ''}|${String(withYear)}`;
  let format = DAY_LABELS.get(cacheKey);
  if (format === undefined) {
    format = new Intl.DateTimeFormat(LOCALE_BY_LANGUAGE[language], {
      timeZone,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      ...(withYear ? { year: 'numeric' } : {}),
    });
    DAY_LABELS.set(cacheKey, format);
  }
  return format.format(instant);
}

/** The calendar day before `key` (`YYYY-MM-DD`), on the calendar, so DST never skips a day. */
function previousDay(key: string): string {
  const date = new Date(`${key}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 1);
  return date.toISOString().slice(0, 10);
}

/**
 * Groups closed positions by the local day they closed on (M6 R7, AC9; mockup Historique), in
 * the order given (newest first, R8). "Today" and "Yesterday" are judged on the trader's clock and
 * time zone, through `Intl`: UTC on the wire, local day on screen. A day of another year shows
 * its year.
 *
 * @example groupByDay(rows, { now: Date.now(), language: 'fr' })[0]?.kind // 'today'
 */
export function groupByDay(
  positions: readonly ClosedPosition[],
  options: DayOptions,
): HistoryDay[] {
  const today = dayKey(options.now, options.timeZone);
  const yesterday = previousDay(today);
  const days: { key: string; kind: DayKind; dateLabel: string; positions: ClosedPosition[] }[] = [];
  for (const position of positions) {
    const instant = Date.parse(position.closedAt);
    const key = dayKey(instant, options.timeZone);
    let day = days.at(-1);
    if (day?.key !== key) {
      const kind: DayKind = key === today ? 'today' : key === yesterday ? 'yesterday' : 'date';
      const withYear = key.slice(0, 4) !== today.slice(0, 4);
      day = { key, kind, dateLabel: dayLabel(instant, options, withYear), positions: [] };
      days.push(day);
    }
    day.positions.push(position);
  }
  return days;
}

/**
 * The rows E9 shows: `newer` (the first page, fetched again when the screen comes back) then
 * `older` (rows already on screen and the pages added by "Show more"), one row per id, the newer
 * copy kept, most recently closed first (M6 R8, AC12). Keeping the rows already on screen means a
 * refresh that pushes rows out of the first page never drops them.
 */
export function mergeHistory(
  newer: readonly ClosedPosition[],
  older: readonly ClosedPosition[],
): ClosedPosition[] {
  const byId = new Map<string, { position: ClosedPosition; closedAt: number }>();
  for (const position of [...newer, ...older]) {
    // Each instant is parsed once here, not twice per comparison in the sort.
    if (!byId.has(position.id)) {
      byId.set(position.id, { position, closedAt: Date.parse(position.closedAt) });
    }
  }
  // ISO 8601 UTC instants: compare as times. `sort` is stable, so equal times keep API order.
  return [...byId.values()].sort((a, b) => b.closedAt - a.closedAt).map(({ position }) => position);
}
